import { env } from './env';
import {
  ErrorEnvelopeSchema,
  type ErrorEnvelope,
} from './schemas/error-envelope';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly errorCode: string,
    public readonly envelope?: ErrorEnvelope,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiCallOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  accessToken?: string | null;
  signal?: AbortSignal;
  /** Internal: set by the retry path to prevent infinite refresh loops. */
  _retried?: boolean;
}

export interface RawApiResponse<T> {
  status: number;
  data: T;
}

/**
 * Refresh hook installed by `AuthProvider` at mount. When `apiCall` receives a
 * 401 with `AUTH_REQUIRED` *and* the request carried an access token, it
 * invokes this hook (coalescing concurrent calls) and retries once with the
 * fresh token. Returns `null` if the refresh path itself failed — the caller
 * then bubbles up the original 401 and the auth context flips to `guest`.
 *
 * Why not `errorCode: 'TOKEN_EXPIRED'` (as the M7b spec drafted)? The backend
 * uses passport-jwt's default behaviour, which emits `AUTH_REQUIRED` for both
 * missing and expired tokens. Distinguishing them server-side would require an
 * auth-guard change; the heuristic "had a token, got AUTH_REQUIRED → refresh"
 * is sufficient and avoids retry storms on legitimate guest 401s.
 */
type RefreshHook = () => Promise<string | null>;
let refreshHook: RefreshHook | null = null;
let inflightRefresh: Promise<string | null> | null = null;

export function installRefreshHook(hook: RefreshHook | null): void {
  refreshHook = hook;
}

async function coalescedRefresh(): Promise<string | null> {
  if (!refreshHook) return null;
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = (async () => {
    try {
      return await refreshHook!();
    } finally {
      inflightRefresh = null;
    }
  })();
  return inflightRefresh;
}

/** Exposed for tests. */
export function __resetApiInternals(): void {
  refreshHook = null;
  inflightRefresh = null;
}

export async function apiCall<T>(
  path: string,
  opts: ApiCallOptions = {},
): Promise<RawApiResponse<T>> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.accessToken) headers.Authorization = `Bearer ${opts.accessToken}`;

  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'include',
  };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
  if (opts.signal) init.signal = opts.signal;

  const res = await fetch(`${env.VITE_PORTAL_API_URL}${path}`, init);

  if (res.status === 204) {
    return { status: res.status, data: undefined as T };
  }

  let parsed: unknown = null;
  try {
    parsed = await res.json();
  } catch {
    parsed = null;
  }

  if (!res.ok) {
    const envelope = ErrorEnvelopeSchema.safeParse(parsed);
    const errorCode = envelope.success ? envelope.data.errorCode : 'UNKNOWN_ERROR';
    const message = envelope.success
      ? envelope.data.message
      : `Request failed (${res.status})`;

    // Auto-refresh exactly once when an authenticated call hits AUTH_REQUIRED.
    const looksExpired =
      res.status === 401 &&
      errorCode === 'AUTH_REQUIRED' &&
      Boolean(opts.accessToken) &&
      !opts._retried &&
      refreshHook !== null;

    if (looksExpired) {
      const nextToken = await coalescedRefresh();
      if (nextToken) {
        return apiCall<T>(path, {
          ...opts,
          accessToken: nextToken,
          _retried: true,
        });
      }
    }

    throw new ApiError(
      message,
      res.status,
      errorCode,
      envelope.success ? envelope.data : undefined,
    );
  }

  return { status: res.status, data: parsed as T };
}
